import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';

import type { LeagueDelayedEntryChip_so5LeaderboardGroup } from './__generated__/index.graphql';

const StyledLabel = styled(LabelS)`
  display: flex;
  align-items: center;
  gap: var(--half-unit);
`;

export const LeagueDelayedEntryChip = ({
  so5LeaderboardGroup,
  showLabel = true,
}: {
  so5LeaderboardGroup: LeagueDelayedEntryChip_so5LeaderboardGroup;
  showLabel?: boolean;
}) => {
  const { cutOffDate } = so5LeaderboardGroup;
  const { message, isEnded } = useTimeLeft(cutOffDate);

  return (
    <LinkOther>
      <Tooltip
        title={
          <Vertical gap={0.5}>
            <LabelS>
              <FormattedMessage
                id="LeagueDelayedEntryChip.tooltip.title"
                defaultMessage="This competition has a later deadline"
              />
            </LabelS>
            <LabelS color="var(--c-nd-600)">
              <FormattedMessage
                id="LeagueDelayedEntryChip.tooltip.deadline"
                defaultMessage="Deadline:"
              />{' '}
              <FormattedDate
                value={cutOffDate}
                day="numeric"
                month="long"
                year="numeric"
                hour="numeric"
                minute="numeric"
              />
            </LabelS>
          </Vertical>
        }
      >
        <div>
          {!isEnded && (
            <Chip>
              <StyledLabel color="var(--c-yellow-400)" as="span" bold>
                <FontAwesomeIcon icon={faClock} /> {message}
              </StyledLabel>
              {!!showLabel && (
                <FormattedMessage
                  id="LeagueDelayEntryChip.laterDeadline"
                  defaultMessage="Later Deadline"
                />
              )}
            </Chip>
          )}
        </div>
      </Tooltip>
    </LinkOther>
  );
};

LeagueDelayedEntryChip.fragments = {
  so5LeaderboardGroup: gql`
    fragment LeagueDelayedEntryChip_so5LeaderboardGroup on So5LeaderboardGroupInterface {
      slug
      cutOffDate
    }
  ` as TypedDocumentNode<LeagueDelayedEntryChip_so5LeaderboardGroup>,
};
